/**
 * <SchoolAssessmentForm> — manual school-assessment fields (organism, L0.1).
 *
 * Controlled by `value` / `onChange` (`UICreateSchoolAssessment`). Subject/chapter catalogs and
 * loading are injected separately — they are reference data, not draft fields.
 */
import React from "react"

import {
    type UICreateSchoolAssessment,
    type UICustomChapter,
    type UISectionedChapter,
    type UISubject,
} from "../../../ports/primary/ui/types"
import { SubjectBadge } from "../../atoms/SubjectBadge"
import { TextInput } from "../../atoms/TextInput"
import { Stack } from "../../atoms/containers/Stack"
import { DatePicker } from "../../molecules/DatePicker"
import { HorizontalSelectInput } from "../../molecules/HorizontalSelectInput"
import { Autocomplete } from "../../molecules/Autocomplete"

export type SchoolAssessmentFormProps = {
    value: UICreateSchoolAssessment
    onChange: (next: UICreateSchoolAssessment) => void
    subjects: UISubject[]
    chapters?: UISectionedChapter[]
    chaptersLoading?: boolean
    disabled?: boolean
    testID?: string
}

function toggleId(ids: Array<string | number>, id: string | number): Array<string | number> {
    const key = String(id)
    return ids.map(String).includes(key) ? ids.filter((x) => String(x) !== key) : [...ids, id]
}

export function SchoolAssessmentForm(props: SchoolAssessmentFormProps) {
    const { value, onChange, subjects, chapters = [], chaptersLoading = false, disabled = false, testID } = props

    const hasChaptersSelected = value.chapterIds.length > 0 || value.customChapters.length > 0

    const patch = (partial: Partial<UICreateSchoolAssessment>) => onChange({ ...value, ...partial })

    return (
        <Stack gap="6" testID={testID}>
            <HorizontalSelectInput
                label="C'est pour quelle matière ?"
                items={subjects}
                getItemId={(s) => s.id}
                renderItem={(subject) => (
                    <SubjectBadge
                        subject={subject}
                        size="md"
                        showLabel
                        selected={subject.id === value.subjectId}
                        onSelect={() => patch({ subjectId: subject.id, chapterIds: [] })}
                        disabled={disabled}
                    />
                )}
            />

            <DatePicker
                label="C'est pour quand ?"
                value={value.date}
                onChange={(date) => patch({ date })}
                disabled={disabled}
            />

            {value.subjectId != null && (
                <Autocomplete
                    label="Sur quels chapitres ?"
                    items={chapters}
                    getItemId={(c) => c.id}
                    getItemLabel={(c) => c.name}
                    selectedIds={value.chapterIds}
                    onToggle={(id) => patch({ chapterIds: toggleId(value.chapterIds, id) })}
                    extras={value.customChapters}
                    allowExtra
                    onAddExtra={(name) => {
                        const id = `custom-${name}`
                        const next: UICustomChapter = { id, label: name }
                        if (value.customChapters.some((e) => e.id === id)) return
                        patch({ customChapters: [...value.customChapters, next] })
                    }}
                    onRemoveExtra={(id) => patch({ customChapters: value.customChapters.filter((e) => e.id !== id) })}
                    groupBy={(c) => (c.parent != null ? { id: c.parent.id, label: c.parent.name } : null)}
                    loading={chaptersLoading}
                    disabled={disabled}
                    searchPlaceholder="Rechercher un chapitre..."
                />
            )}

            {hasChaptersSelected && (
                <TextInput
                    label="Détails"
                    value={value.keyRequirementsText}
                    onChangeText={(keyRequirementsText) => patch({ keyRequirementsText })}
                    placeholder="Ex: ce qu'il faut connaître, savoir-faire..."
                    multiline
                    numberOfLines={4}
                    disabled={disabled}
                />
            )}
        </Stack>
    )
}
